'use client';

import { useState, useEffect } from 'react';
import ProcessFilters from './ProcessFilters';
import ProcessTable from './ProcessTable';
import BulkActionBar from './BulkActionBar';
import Icon from '@/components/ui/AppIcon';

interface Process {
  id: string;
  name: string;
  owner: string;
  status: 'draft' | 'active' | 'completed' | 'suspended' | 'archived';
  department: string;
  createdDate: string;
  lastActivity: string;
  instanceCount: number;
  avgCompletionTime: string;
  successRate: number;
  budget?: number;
  resourceAllocation?: string;
  syncStatus?: 'synced' | 'pending' | 'error';
}

interface FilterState {
  departments: string[];
  statuses: string[];
  dateRange: {
    start: string;
    end: string;
  };
  owner: string;
  searchTerm: string;
}

const ProcessManagementInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedProcesses, setSelectedProcesses] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<FilterState>({
    departments: [],
    statuses: [],
    dateRange: { start: '', end: '' },
    owner: '',
    searchTerm: '',
  });
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFormat, setExportFormat] = useState<'csv' | 'excel'>('csv');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const mockProcesses: Process[] = [
    {
      id: 'PRC-2024-001',
      name: 'Aprovação de Despesas Corporativas',
      owner: 'Maria Silva',
      status: 'active',
      department: 'Financeiro',
      createdDate: '15/11/2024',
      lastActivity: '07/12/2024',
      instanceCount: 342,
      avgCompletionTime: '2.5 dias',
      successRate: 94,
      budget: 150000,
      resourceAllocation: '5 usuários',
      syncStatus: 'synced',
    },
    {
      id: 'PRC-2024-002',
      name: 'Onboarding de Novos Funcionários',
      owner: 'João Santos',
      status: 'active',
      department: 'Recursos Humanos',
      createdDate: '20/10/2024',
      lastActivity: '08/12/2024',
      instanceCount: 128,
      avgCompletionTime: '5.2 dias',
      successRate: 89,
      budget: 85000,
      resourceAllocation: '8 usuários',
      syncStatus: 'synced',
    },
    {
      id: 'PRC-2024-003',
      name: 'Solicitação de Compras',
      owner: 'Ana Costa',
      status: 'active',
      department: 'Operações',
      createdDate: '05/09/2024',
      lastActivity: '08/12/2024',
      instanceCount: 567,
      avgCompletionTime: '3.8 dias',
      successRate: 92,
      budget: 220000,
      resourceAllocation: '12 usuários',
      syncStatus: 'pending',
    },
    {
      id: 'PRC-2024-004',
      name: 'Gestão de Incidentes de TI',
      owner: 'Carlos Oliveira',
      status: 'active',
      department: 'Tecnologia da Informação',
      createdDate: '12/08/2024',
      lastActivity: '08/12/2024',
      instanceCount: 891,
      avgCompletionTime: '1.2 dias',
      successRate: 96,
      budget: 95000,
      resourceAllocation: '6 usuários',
      syncStatus: 'synced',
    },
    {
      id: 'PRC-2024-005',
      name: 'Aprovação de Férias',
      owner: 'Paula Mendes',
      status: 'active',
      department: 'Recursos Humanos',
      createdDate: '18/07/2024',
      lastActivity: '07/12/2024',
      instanceCount: 234,
      avgCompletionTime: '1.5 dias',
      successRate: 98,
      budget: 45000,
      resourceAllocation: '4 usuários',
      syncStatus: 'synced',
    },
    {
      id: 'PRC-2024-006',
      name: 'Processo de Auditoria Interna',
      owner: 'Roberto Lima',
      status: 'suspended',
      department: 'Financeiro',
      createdDate: '22/06/2024',
      lastActivity: '15/11/2024',
      instanceCount: 45,
      avgCompletionTime: '12.5 dias',
      successRate: 87,
      budget: 180000,
      resourceAllocation: '10 usuários',
      syncStatus: 'error',
    },
    {
      id: 'PRC-2024-007',
      name: 'Gestão de Contratos',
      owner: 'Fernanda Rocha',
      status: 'active',
      department: 'Operações',
      createdDate: '10/05/2024',
      lastActivity: '08/12/2024',
      instanceCount: 156,
      avgCompletionTime: '7.3 dias',
      successRate: 91,
      budget: 125000,
      resourceAllocation: '7 usuários',
      syncStatus: 'synced',
    },
    {
      id: 'PRC-2024-008',
      name: 'Solicitação de Suporte Técnico',
      owner: 'Marcos Ferreira',
      status: 'active',
      department: 'Tecnologia da Informação',
      createdDate: '03/04/2024',
      lastActivity: '08/12/2024',
      instanceCount: 1234,
      avgCompletionTime: '0.8 dias',
      successRate: 95,
      budget: 75000,
      resourceAllocation: '5 usuários',
      syncStatus: 'synced',
    },
    {
      id: 'PRC-2024-009',
      name: 'Processo de Recrutamento',
      owner: 'Juliana Alves',
      status: 'active',
      department: 'Recursos Humanos',
      createdDate: '28/03/2024',
      lastActivity: '07/12/2024',
      instanceCount: 89,
      avgCompletionTime: '15.2 dias',
      successRate: 85,
      budget: 110000,
      resourceAllocation: '9 usuários',
      syncStatus: 'synced',
    },
    {
      id: 'PRC-2024-010',
      name: 'Controle de Qualidade',
      owner: 'Ricardo Souza',
      status: 'active',
      department: 'Operações',
      createdDate: '14/02/2024',
      lastActivity: '08/12/2024',
      instanceCount: 445,
      avgCompletionTime: '4.1 dias',
      successRate: 93,
      budget: 165000,
      resourceAllocation: '11 usuários',
      syncStatus: 'synced',
    },
    {
      id: 'PRC-2024-011',
      name: 'Gestão de Fornecedores',
      owner: 'Beatriz Martins',
      status: 'draft',
      department: 'Operações',
      createdDate: '05/12/2024',
      lastActivity: '06/12/2024',
      instanceCount: 0,
      avgCompletionTime: '-',
      successRate: 0,
      budget: 95000,
      resourceAllocation: '6 usuários',
      syncStatus: 'pending',
    },
    {
      id: 'PRC-2024-012',
      name: 'Aprovação de Orçamento Anual',
      owner: 'Eduardo Campos',
      status: 'completed',
      department: 'Financeiro',
      createdDate: '10/01/2024',
      lastActivity: '30/11/2024',
      instanceCount: 12,
      avgCompletionTime: '18.5 dias',
      successRate: 100,
      budget: 250000,
      resourceAllocation: '15 usuários',
      syncStatus: 'synced',
    },
    {
      id: 'PRC-2024-013',
      name: 'Gestão de Mudanças de Sistema',
      owner: 'Luciana Barbosa',
      status: 'active',
      department: 'Tecnologia da Informação',
      createdDate: '25/01/2024',
      lastActivity: '08/12/2024',
      instanceCount: 178,
      avgCompletionTime: '6.7 dias',
      successRate: 88,
      budget: 135000,
      resourceAllocation: '8 usuários',
      syncStatus: 'synced',
    },
    {
      id: 'PRC-2024-014',
      name: 'Processo de Folha de Pagamento',
      owner: 'André Pereira',
      status: 'active',
      department: 'Recursos Humanos',
      createdDate: '08/02/2024',
      lastActivity: '08/12/2024',
      instanceCount: 24,
      avgCompletionTime: '2.1 dias',
      successRate: 99,
      budget: 200000,
      resourceAllocation: '5 usuários',
      syncStatus: 'synced',
    },
    {
      id: 'PRC-2024-015',
      name: 'Gestão de Estoque',
      owner: 'Camila Rodrigues',
      status: 'active',
      department: 'Operações',
      createdDate: '19/03/2024',
      lastActivity: '08/12/2024',
      instanceCount: 678,
      avgCompletionTime: '3.2 dias',
      successRate: 94,
      budget: 145000,
      resourceAllocation: '9 usuários',
      syncStatus: 'synced',
    },
    {
      id: 'PRC-2024-016',
      name: 'Solicitação de Reembolso',
      owner: 'Gabriel Nunes',
      status: 'active',
      department: 'Financeiro',
      createdDate: '02/04/2024',
      lastActivity: '08/12/2024',
      instanceCount: 423,
      avgCompletionTime: '2.8 dias',
      successRate: 96,
      budget: 85000,
      resourceAllocation: '4 usuários',
      syncStatus: 'synced',
    },
    {
      id: 'PRC-2024-017',
      name: 'Gestão de Projetos',
      owner: 'Patrícia Gomes',
      status: 'active',
      department: 'Operações',
      createdDate: '16/05/2024',
      lastActivity: '07/12/2024',
      instanceCount: 67,
      avgCompletionTime: '25.4 dias',
      successRate: 82,
      budget: 320000,
      resourceAllocation: '18 usuários',
      syncStatus: 'synced',
    },
    {
      id: 'PRC-2024-018',
      name: 'Backup e Recuperação de Dados',
      owner: 'Thiago Cardoso',
      status: 'active',
      department: 'Tecnologia da Informação',
      createdDate: '29/06/2024',
      lastActivity: '08/12/2024',
      instanceCount: 365,
      avgCompletionTime: '0.5 dias',
      successRate: 99,
      budget: 95000,
      resourceAllocation: '3 usuários',
      syncStatus: 'synced',
    },
    {
      id: 'PRC-2024-019',
      name: 'Avaliação de Desempenho',
      owner: 'Renata Dias',
      status: 'suspended',
      department: 'Recursos Humanos',
      createdDate: '11/07/2024',
      lastActivity: '20/11/2024',
      instanceCount: 156,
      avgCompletionTime: '8.9 dias',
      successRate: 86,
      budget: 75000,
      resourceAllocation: '7 usuários',
      syncStatus: 'pending',
    },
    {
      id: 'PRC-2024-020',
      name: 'Gestão de Riscos',
      owner: 'Felipe Moreira',
      status: 'active',
      department: 'Financeiro',
      createdDate: '23/08/2024',
      lastActivity: '08/12/2024',
      instanceCount: 89,
      avgCompletionTime: '11.3 dias',
      successRate: 90,
      budget: 185000,
      resourceAllocation: '12 usuários',
      syncStatus: 'synced',
    },
    {
      id: 'PRC-2024-021',
      name: 'Processo de Logística',
      owner: 'Vanessa Castro',
      status: 'active',
      department: 'Operações',
      createdDate: '07/09/2024',
      lastActivity: '08/12/2024',
      instanceCount: 534,
      avgCompletionTime: '4.6 dias',
      successRate: 91,
      budget: 175000,
      resourceAllocation: '10 usuários',
      syncStatus: 'synced',
    },
    {
      id: 'PRC-2024-022',
      name: 'Gestão de Licenças de Software',
      owner: 'Bruno Teixeira',
      status: 'archived',
      department: 'Tecnologia da Informação',
      createdDate: '15/01/2024',
      lastActivity: '30/10/2024',
      instanceCount: 45,
      avgCompletionTime: '3.2 dias',
      successRate: 95,
      budget: 125000,
      resourceAllocation: '4 usuários',
      syncStatus: 'synced',
    },
  ];

  const userRole: 'user' | 'manager' | 'admin' = 'manager';

  const filteredProcesses = mockProcesses.filter((process) => {
    if (filters.departments.length > 0 && !filters.departments.includes(process.department.toLowerCase())) {
      return false;
    }
    if (filters.statuses.length > 0 && !filters.statuses.includes(process.status)) {
      return false;
    }
    if (filters.owner && !process.owner.toLowerCase().includes(filters.owner.toLowerCase())) {
      return false;
    }
    if (searchQuery && !process.name.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    return true;
  });

  const handleProcessAction = (action: string, processId: string) => {
    console.log(`Action: ${action} on process: ${processId}`);
  };

  const handleBulkAction = (action: string) => {
    console.log(`Bulk action: ${action} on ${selectedProcesses.length} processes`);
    if (action === 'export') {
      setShowExportModal(true);
    }
  };

  const handleExport = () => {
    console.log(`Exporting ${selectedProcesses.length} processes as ${exportFormat}`);
    setShowExportModal(false);
    setSelectedProcesses([]);
  };

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:424:6" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="424" data-component-file="ProcessManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%20flex%20flex-col%22%7D" className="flex-1 flex flex-col">
        <div data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:425:8" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="425" data-component-file="ProcessManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-surface%20border-b%20border-border%20px-6%20py-4%22%7D" className="bg-surface border-b border-border px-6 py-4">
          <div data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:426:10" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="426" data-component-file="ProcessManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-10%20bg-muted%20rounded-md%20animate-pulse%22%7D" className="h-10 bg-muted rounded-md animate-pulse" />
        </div>
        <div data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:428:8" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="428" data-component-file="ProcessManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%20flex%22%7D" className="flex-1 flex">
          <div data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:429:10" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="429" data-component-file="ProcessManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-80%20bg-surface%20border-r%20border-border%20p-4%22%7D" className="w-80 bg-surface border-r border-border p-4">
            <div data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:430:12" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="430" data-component-file="ProcessManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-4%22%7D" className="space-y-4">
              <div data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:431:14" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="431" data-component-file="ProcessManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-8%20bg-muted%20rounded-md%20animate-pulse%22%7D" className="h-8 bg-muted rounded-md animate-pulse" />
              <div data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:432:14" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="432" data-component-file="ProcessManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-32%20bg-muted%20rounded-md%20animate-pulse%22%7D" className="h-32 bg-muted rounded-md animate-pulse" />
            </div>
          </div>
          <div data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:435:10" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="435" data-component-file="ProcessManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%20p-6%22%7D" className="flex-1 p-6">
            <div data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:436:12" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="436" data-component-file="ProcessManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-96%20bg-muted%20rounded-md%20animate-pulse%22%7D" className="h-96 bg-muted rounded-md animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:444:4" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="444" data-component-file="ProcessManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%20flex%20flex-col%22%7D" className="flex-1 flex flex-col">
      <div data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:445:6" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="445" data-component-file="ProcessManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-surface%20border-b%20border-border%20px-6%20py-4%22%7D" className="bg-surface border-b border-border px-6 py-4">
        <div data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:446:8" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="446" data-component-file="ProcessManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-4%22%7D" className="flex items-center gap-4">
          <div data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:447:10" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="447" data-component-file="ProcessManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%20relative%22%7D" className="flex-1 relative">
            <Icon data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:448:12" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="448" data-component-file="ProcessManagementInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22MagnifyingGlassIcon%22%2C%22className%22%3A%22absolute%20left-3%20top-1%2F2%20-translate-y-1%2F2%20text-muted-foreground%22%7D"
              name="MagnifyingGlassIcon"
              size={20}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <input data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:453:12" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="453" data-component-file="ProcessManagementInteractive.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22text%22%2C%22value%22%3A%22%5Bvar%3AsearchQuery%5D%22%2C%22className%22%3A%22w-full%20pl-10%20pr-4%20py-2.5%20bg-muted%20border%20border-border%20rounded-md%20text-sm%20focus%3Aoutline-none%20focus%3Aring-2%20focus%3Aring-ring%22%7D"
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar processos por nome, ID, proprietário..."
              className="w-full pl-10 pr-4 py-2.5 bg-muted border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <button data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:461:10" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="461" data-component-file="ProcessManagementInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22flex%20items-center%20gap-2%20px-4%20py-2.5%20bg-secondary%20text-secondary-foreground%20rounded-md%20hover%3Abg-secondary%2F90%20transition-micro%22%7D"
            onClick={() => setShowExportModal(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/90 transition-micro"
          >
            <Icon data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:465:12" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="465" data-component-file="ProcessManagementInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ArrowDownTrayIcon%22%7D" name="ArrowDownTrayIcon" size={20} />
            <span data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:466:12" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="466" data-component-file="ProcessManagementInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-sm%20font-medium%22%2C%22textContent%22%3A%22Exportar%22%7D" className="text-sm font-medium">Exportar</span>
          </button>
        </div>
      </div>

      <div data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:471:6" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="471" data-component-file="ProcessManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%20flex%20overflow-hidden%22%7D" className="flex-1 flex overflow-hidden">
        <div data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:472:8" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="472" data-component-file="ProcessManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-80%20flex-shrink-0%20overflow-y-auto%22%7D" className="w-80 flex-shrink-0 overflow-y-auto">
          <ProcessFilters data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:473:10" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="473" data-component-file="ProcessManagementInteractive.tsx" data-component-name="ProcessFilters" data-component-content="%7B%22elementName%22%3A%22ProcessFilters%22%7D" onFilterChange={setFilters} activeFilters={filters} />
        </div>

        <div data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:476:8" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="476" data-component-file="ProcessManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%20overflow-auto%20p-6%22%7D" className="flex-1 overflow-auto p-6">
          <div data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:477:10" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="477" data-component-file="ProcessManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-4%20flex%20items-center%20justify-between%22%7D" className="mb-4 flex items-center justify-between">
            <div data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:478:12" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="478" data-component-file="ProcessManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-sm%20text-muted-foreground%22%2C%22textContent%22%3A%22Mostrando%20de%20%20%20processos%22%7D" className="text-sm text-muted-foreground">
              Mostrando <span data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:479:24" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="479" data-component-file="ProcessManagementInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-semibold%20text-foreground%22%7D" className="font-semibold text-foreground">{filteredProcesses.length}</span> de{' '}
              <span data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:480:14" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="480" data-component-file="ProcessManagementInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-semibold%20text-foreground%22%7D" className="font-semibold text-foreground">{mockProcesses.length}</span> processos
            </div>
          </div>

          <div data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:484:10" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="484" data-component-file="ProcessManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-surface%20border%20border-border%20rounded-lg%20overflow-hidden%22%7D" className="bg-surface border border-border rounded-lg overflow-hidden">
            <ProcessTable data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:485:12" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="485" data-component-file="ProcessManagementInteractive.tsx" data-component-name="ProcessTable" data-component-content="%7B%22elementName%22%3A%22ProcessTable%22%7D"
              processes={filteredProcesses}
              selectedProcesses={selectedProcesses}
              onSelectionChange={setSelectedProcesses}
              onProcessAction={handleProcessAction}
              userRole={userRole}
            />
          </div>
        </div>
      </div>

      <BulkActionBar data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:496:6" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="496" data-component-file="ProcessManagementInteractive.tsx" data-component-name="BulkActionBar" data-component-content="%7B%22elementName%22%3A%22BulkActionBar%22%7D"
        selectedCount={selectedProcesses.length}
        onAction={handleBulkAction}
        onClearSelection={() => setSelectedProcesses([])}
      />

      {showExportModal && (
        <>
          <div data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:504:10" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="504" data-component-file="ProcessManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22fixed%20inset-0%20bg-foreground%2F50%20z-50%22%7D" className="fixed inset-0 bg-foreground/50 z-50" onClick={() => setShowExportModal(false)} />
          <div data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:505:10" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="505" data-component-file="ProcessManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22fixed%20top-1%2F2%20left-1%2F2%20-translate-x-1%2F2%20-translate-y-1%2F2%20bg-surface%20border%20border-border%20rounded-lg%20shadow-elevated%20z-50%20w-full%20max-w-md%22%7D" className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-surface border border-border rounded-lg shadow-elevated z-50 w-full max-w-md">
            <div data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:506:12" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="506" data-component-file="ProcessManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-6%20border-b%20border-border%22%7D" className="p-6 border-b border-border">
              <h3 data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:507:14" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="507" data-component-file="ProcessManagementInteractive.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-lg%20font-semibold%20text-foreground%22%2C%22textContent%22%3A%22Exportar%20Processos%22%7D" className="text-lg font-semibold text-foreground">Exportar Processos</h3>
            </div>
            <div data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:509:12" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="509" data-component-file="ProcessManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-6%20space-y-4%22%7D" className="p-6 space-y-4">
              <div data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:510:14" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="510" data-component-file="ProcessManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
                <label data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:511:16" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="511" data-component-file="ProcessManagementInteractive.tsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22block%20text-sm%20font-medium%20text-foreground%20mb-2%22%2C%22textContent%22%3A%22Formato%20de%20Exporta%C3%A7%C3%A3o%22%7D" className="block text-sm font-medium text-foreground mb-2">
                  Formato de Exportação
                </label>
                <div data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:514:16" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="514" data-component-file="ProcessManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-2%22%7D" className="space-y-2">
                  <label data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:515:18" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="515" data-component-file="ProcessManagementInteractive.tsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22flex%20items-center%20gap-3%20p-3%20border%20border-border%20rounded-md%20cursor-pointer%20hover%3Abg-muted%20transition-micro%22%7D" className="flex items-center gap-3 p-3 border border-border rounded-md cursor-pointer hover:bg-muted transition-micro">
                    <input data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:516:20" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="516" data-component-file="ProcessManagementInteractive.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22radio%22%2C%22name%22%3A%22exportFormat%22%2C%22value%22%3A%22csv%22%2C%22className%22%3A%22w-4%20h-4%20text-primary%20focus%3Aring-2%20focus%3Aring-ring%22%7D"
                      type="radio"
                      name="exportFormat"
                      value="csv"
                      checked={exportFormat === 'csv'}
                      onChange={(e) => setExportFormat(e.target.value as 'csv' | 'excel')}
                      className="w-4 h-4 text-primary focus:ring-2 focus:ring-ring"
                    />
                    <div data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:524:20" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="524" data-component-file="ProcessManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%22%7D" className="flex-1">
                      <div data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:525:22" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="525" data-component-file="ProcessManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-sm%20font-medium%20text-foreground%22%2C%22textContent%22%3A%22CSV%22%7D" className="text-sm font-medium text-foreground">CSV</div>
                      <div data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:526:22" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="526" data-component-file="ProcessManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-xs%20text-muted-foreground%22%2C%22textContent%22%3A%22Arquivo%20de%20valores%20separados%20por%20v%C3%ADrgula%22%7D" className="text-xs text-muted-foreground">
                        Arquivo de valores separados por vírgula
                      </div>
                    </div>
                  </label>
                  <label data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:531:18" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="531" data-component-file="ProcessManagementInteractive.tsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22flex%20items-center%20gap-3%20p-3%20border%20border-border%20rounded-md%20cursor-pointer%20hover%3Abg-muted%20transition-micro%22%7D" className="flex items-center gap-3 p-3 border border-border rounded-md cursor-pointer hover:bg-muted transition-micro">
                    <input data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:532:20" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="532" data-component-file="ProcessManagementInteractive.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22radio%22%2C%22name%22%3A%22exportFormat%22%2C%22value%22%3A%22excel%22%2C%22className%22%3A%22w-4%20h-4%20text-primary%20focus%3Aring-2%20focus%3Aring-ring%22%7D"
                      type="radio"
                      name="exportFormat"
                      value="excel"
                      checked={exportFormat === 'excel'}
                      onChange={(e) => setExportFormat(e.target.value as 'csv' | 'excel')}
                      className="w-4 h-4 text-primary focus:ring-2 focus:ring-ring"
                    />
                    <div data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:540:20" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="540" data-component-file="ProcessManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%22%7D" className="flex-1">
                      <div data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:541:22" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="541" data-component-file="ProcessManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-sm%20font-medium%20text-foreground%22%2C%22textContent%22%3A%22Excel%22%7D" className="text-sm font-medium text-foreground">Excel</div>
                      <div data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:542:22" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="542" data-component-file="ProcessManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-xs%20text-muted-foreground%22%2C%22textContent%22%3A%22Planilha%20do%20Microsoft%20Excel%20(.xlsx)%22%7D" className="text-xs text-muted-foreground">
                        Planilha do Microsoft Excel (.xlsx)
                      </div>
                    </div>
                  </label>
                </div>
              </div>
              <div data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:549:14" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="549" data-component-file="ProcessManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-muted%20p-3%20rounded-md%22%7D" className="bg-muted p-3 rounded-md">
                <div data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:550:16" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="550" data-component-file="ProcessManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-xs%20text-muted-foreground%22%7D" className="text-xs text-muted-foreground">
                  {selectedProcesses.length > 0
                    ? `${selectedProcesses.length} processo(s) selecionado(s) será(ão) exportado(s)`
                    : 'Todos os processos filtrados serão exportados'}
                </div>
              </div>
            </div>
            <div data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:557:12" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="557" data-component-file="ProcessManagementInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-6%20border-t%20border-border%20flex%20gap-3%22%7D" className="p-6 border-t border-border flex gap-3">
              <button data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:558:14" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="558" data-component-file="ProcessManagementInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22flex-1%20px-4%20py-2%20bg-muted%20text-foreground%20rounded-md%20hover%3Abg-muted%2F80%20transition-micro%22%2C%22textContent%22%3A%22Cancelar%22%7D"
                onClick={() => setShowExportModal(false)}
                className="flex-1 px-4 py-2 bg-muted text-foreground rounded-md hover:bg-muted/80 transition-micro"
              >
                Cancelar
              </button>
              <button data-component-id="src/app/process-management/components/ProcessManagementInteractive.tsx:564:14" data-component-path="src/app/process-management/components/ProcessManagementInteractive.tsx" data-component-line="564" data-component-file="ProcessManagementInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22flex-1%20px-4%20py-2%20bg-primary%20text-primary-foreground%20rounded-md%20hover%3Abg-primary%2F90%20transition-micro%22%2C%22textContent%22%3A%22Exportar%22%7D"
                onClick={handleExport}
                className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-micro"
              >
                Exportar
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ProcessManagementInteractive;